/** Dettaglio di un briefing di mercato: sintesi LLM aggregata + report titolo collegati (SPEC-010 §8.5). */

import { Link, useParams } from 'react-router-dom';
import { ApiError } from '@/lib/api-client';
import { formatDate, formatDateTime } from '@/lib/format';
import { Alert, Badge, Card, Spinner } from '@/ui';
import { Markdown } from '@/ui/Markdown';
import { AgenticBadge } from '@/features/agent-runs/AgenticBadge';
import { useBriefingDetail, type BriefingDetail, type DeferredAnalysisRequest } from './api';
import { BRIEFING_STATUS_LABELS, BRIEFING_STATUS_TONE } from './market-status-format';
import './markets.css';

/**
 * Un briefing vuoto non è un guasto dell'LLM: prima lo si annunciava sempre
 * come "connettore LLM non disponibile" anche quando l'LLM non era mai stato
 * interpellato, perché non c'era nulla da riassumere (quota AI esaurita).
 */
function briefingSubtitle(briefing: BriefingDetail): string {
  switch (briefing.fullReport?.emptyReason) {
    case 'quota_esaurita':
      return 'Le analisi non sono mai partite: quota giornaliera di analisi AI esaurita';
    case 'analisi_fallite':
      return 'Tutte le analisi richieste sono fallite';
    case 'nessuna_analisi_pronta':
      return 'Nessuna analisi completata entro l\'apertura del mercato';
    default:
      return briefing.fullReport?.generatedByLlm
        ? 'Generato dal connettore LLM'
        : 'Riepilogo testuale semplice: connettore LLM non disponibile al momento della generazione';
  }
}

/** Come è andata ciascuna analisi chiesta dallo studio (F-199). */
function requestOutcomeLabel(request: DeferredAnalysisRequest): string {
  if (request.outcome === 'quota_exceeded') return 'non partita: quota esaurita';
  if (request.outcome === 'failed' || request.outcome === 'requesting') return "non partita: errore all'avvio";
  if (request.analysisStatus === 'completed') return request.outcome === 'reused' ? 'conclusa (riusata)' : 'conclusa';
  if (request.analysisStatus === 'failed') return 'fallita';
  if (request.analysisStatus === 'pending' || request.analysisStatus === 'running') return "non conclusa entro l'ora limite";
  return 'nessuna analisi trovata';
}

/**
 * F-199: il resoconto agentico con analisi differita. Due run (la prima
 * esecuzione e, se ci sono state analisi, la ripresa) e le analisi che lo
 * studio ha scelto di chiedere, con il motivo: è ciò che spiega perché oggi
 * sono stati analizzati quei titoli e non altri.
 */
function DeferredStudyCard({ report }: { report: NonNullable<BriefingDetail['fullReport']> }) {
  const requests = report.analysisRequests ?? [];
  return (
    <Card
      title="Studio agentico con analisi differita"
      subtitle="Il resoconto è partito subito come studio: il modello ha letto il mercato e ha chiesto un'analisi approfondita solo dove serviva, al massimo su 3 titoli, riprendendo una volta quando erano concluse."
    >
      <p className="markets__mode-hint">
        {report.firstRunId ? <Link to={`/agent-runs/${report.firstRunId}`}>Prima esecuzione →</Link> : 'Prima esecuzione non registrata.'}
        {report.resumeRunId && (
          <>
            {' · '}
            <Link to={`/agent-runs/${report.resumeRunId}`}>Ripresa →</Link>
          </>
        )}
        {report.fallback === 'standard_summary' && " · la ripresa non ha concluso: il testo è il riepilogo standard delle analisi"}
      </p>
      {requests.length === 0 ? (
        <p className="text-muted">Nessuna analisi approfondita chiesta: il resoconto si è chiuso alla prima esecuzione.</p>
      ) : (
        <ul className="briefing-chain">
          {requests.map((request) => (
            <li key={request.slot}>
              <Badge tone={request.analysisStatus === 'completed' ? 'positive' : 'neutral'}>{requestOutcomeLabel(request)}</Badge>
              <span>
                <strong>{request.symbol ?? request.instrumentId}</strong>
                <span className="briefing-chain__detail">{request.reason}</span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

export function BriefingDetailPage() {
  const { mic, briefingId } = useParams<{ mic: string; briefingId: string }>();
  const detailQuery = useBriefingDetail(mic ?? '', briefingId ?? '', Boolean(mic && briefingId));

  if (detailQuery.isPending) {
    return (
      <div className="markets">
        <Spinner label="Caricamento del briefing…" />
      </div>
    );
  }
  if (detailQuery.isError) {
    return (
      <div className="markets">
        <Alert variant="error" title="Impossibile caricare il briefing">
          {detailQuery.error instanceof ApiError ? detailQuery.error.message : 'Errore imprevisto.'}
        </Alert>
      </div>
    );
  }

  const briefing = detailQuery.data;
  const analyses = briefing.fullReport?.analyses ?? [];
  const inWishlist = new Set(briefing.wishlistInstrumentIds ?? []);
  // Il prezzo dell'istante viene dal backend, che lo cerca fra gli snapshot
  // precedenti al resoconto: qui si indicizza soltanto.
  const priceById = new Map((briefing.instruments ?? []).map((entry) => [entry.instrumentId, entry]));
  const chain = briefing.chain;

  return (
    <div className="markets">
      <header className="markets__header">
        <div>
          <Link to={`/markets/${mic}`} className="markets__back">
            ← Torna al mercato
          </Link>
          <h1 className="markets__title">
            Briefing {mic} — {formatDate(briefing.briefingDate)}
          </h1>
          <p className="markets__subtitle">
            {briefing.instrumentCount} titoli · {briefing.analysesCompletedCount} completate ·{' '}
            {briefing.analysesFailedCount} fallite · {briefing.analysesSkippedCount} saltate
          </p>
        </div>
        <span className="markets__header-actions">
          {briefing.fullReport?.mode === 'agentic' && <AgenticBadge />}
          <Badge tone={BRIEFING_STATUS_TONE[briefing.status]}>{BRIEFING_STATUS_LABELS[briefing.status]}</Badge>
        </span>
      </header>

      {briefing.fullReport?.variant === 'deferred_analyses' && <DeferredStudyCard report={briefing.fullReport} />}

      {briefing.fullReport?.mode === 'agentic' && briefing.fullReport.variant !== 'deferred_analyses' && (
        <Card
          title="Studio agentico"
          subtitle="Questa revisione non è una chiamata sola: il modello ha interrogato gli strumenti e consegnato da sé le proposte al motore. Il run conserva prompt, ragionamento e ogni chiamata."
        >
          <p className="markets__mode-hint">
            {briefing.fullReport.agentRunId ? (
              <Link to={`/agent-runs/${briefing.fullReport.agentRunId}`}>Apri il run passo per passo →</Link>
            ) : (
              'Il run non è stato registrato.'
            )}
            {typeof briefing.fullReport.proposalsSubmitted === 'number' && (
              <> · {briefing.fullReport.proposalsSubmitted} proposte consegnate</>
            )}
            {typeof briefing.fullReport.gateBlocks === 'number' && briefing.fullReport.gateBlocks > 0 && (
              <> · {briefing.fullReport.gateBlocks} bloccate dal cancello</>
            )}
            {briefing.fullReport.costUsd && <> · {briefing.fullReport.costUsd} USD stimati</>}
          </p>
        </Card>
      )}

      {briefing.summaryMarkdown && (
        <Card title={briefing.fullReport?.emptyReason ? 'Perché non c\'è un riepilogo' : 'Riepilogo'} subtitle={briefingSubtitle(briefing)}>
          <Markdown content={briefing.summaryMarkdown} />
        </Card>
      )}

      {briefing.sentToTelegramAt && (
        <p className="text-muted">Inviato via Telegram il {formatDateTime(briefing.sentToTelegramAt)}</p>
      )}

      {chain && (
        <Card
          title="Cosa è partito da qui"
          subtitle="Un resoconto non produce solo testo: da qui partono analisi a pagamento e, con la conferma, una catena che può finire in un ordine."
        >
          <ul className="briefing-chain">
            <li>
              <Badge tone={chain.analysesTriggered > 0 ? 'accent' : 'neutral'}>
                {chain.analysesTriggered > 0 ? 'attivate' : 'nessuna'}
              </Badge>
              <span>
                <strong>Analisi AI per titolo</strong>
                <span className="briefing-chain__detail">
                  {chain.analysesTriggered > 0
                    ? `${chain.analysesTriggered} avviate · ${chain.analysesCompleted} completate · ${chain.analysesFailed} fallite`
                    : 'Nessuna analisi avviata da questo resoconto.'}
                </span>
              </span>
            </li>

            <li>
              {/* La distinzione che questa riga esiste per fare: senza la spunta
                  un resoconto di mercato NON porta a nessun ordine, e il fatto
                  che le analisi siano partite non cambia niente. */}
              <Badge tone={chain.autoTradingRequested ? 'accent' : 'positive'}>
                {chain.autoTradingRequested ? 'richiesto' : 'non richiesto'}
              </Badge>
              <span>
                <strong>Catena verso l&apos;auto-trading</strong>
                <span className="briefing-chain__detail">
                  {chain.autoTradingRequested
                    ? 'Alla chiusura è stata accodata la generazione del digest di portafoglio e la valutazione.'
                    : 'La spunta di conferma non era attiva: da questo resoconto non è partita nessuna valutazione di acquisto.'}
                </span>
              </span>
            </li>

            {/* Digest e run sono «dello stesso giorno», non «generati da qui»:
                il digest nasce anche da solo, e non esiste una colonna che li
                colleghi. Dirlo diversamente sarebbe attribuire una causa che il
                dato non dimostra. */}
            <li>
              <Badge tone={chain.digest ? 'accent' : 'neutral'}>
                {chain.digest ? chain.digest.status : 'assente'}
              </Badge>
              <span>
                <strong>
                  {chain.digest ? (
                    // Apribile: e' il documento da cui il motore ricava le
                    // proposte, quindi la domanda «cosa diceva?» e' immediata.
                    <Link to={`/briefing?date=${briefing.briefingDate}`}>
                      Digest di portafoglio dello stesso giorno →
                    </Link>
                  ) : (
                    'Digest di portafoglio dello stesso giorno'
                  )}
                </strong>
                <span className="briefing-chain__detail">
                  {chain.digest
                    ? 'È il documento da cui il motore ricava le proposte.'
                    : 'Nessun digest per questa data: senza, il motore non ha da cosa partire.'}
                </span>
              </span>
            </li>

            <li>
              <Badge tone={chain.autoTradeRun ? (chain.autoTradeRun.executedCount > 0 ? 'negative' : 'accent') : 'neutral'}>
                {chain.autoTradeRun ? chain.autoTradeRun.status : 'nessuna'}
              </Badge>
              <span>
                <strong>Valutazione di auto-trading dello stesso giorno</strong>
                <span className="briefing-chain__detail">
                  {chain.autoTradeRun
                    ? `${chain.autoTradeRun.executedCount} eseguite · ${chain.autoTradeRun.rejectedCount} scartate`
                    : 'Nessuna valutazione registrata per questa data.'}
                </span>
                {/* Le singole operazioni, apribili: il conteggio dice che
                    qualcosa e' successo, non dove guardare — e «perche' questa
                    non e' andata a buon fine» e' la domanda piu' frequente. */}
                {chain.proposals.length > 0 && (
                  <span className="briefing-chain__proposals">
                    {chain.proposals.map((proposal) => (
                      <Link
                        key={proposal.id}
                        to={`/auto-trading/proposals/${proposal.id}`}
                        className="briefing-chain__proposal"
                        title={proposal.rejectionReasonCode ?? undefined}
                      >
                        <Badge tone={proposal.status === 'executed' ? 'positive' : 'neutral'}>
                          {proposal.status}
                        </Badge>
                        {proposal.side === 'buy' ? 'Acquisto' : 'Vendita'} {proposal.symbol ?? ''}
                      </Link>
                    ))}
                  </span>
                )}
              </span>
            </li>
          </ul>
        </Card>
      )}

      {analyses.length > 0 && (
        <Card title="Report per titolo" flush>
          <ul className="ai-analysis__list">
            {analyses.map((analysis) => (
              <li key={analysis.analysisId} className="ai-analysis__item">
                <div className="ai-analysis__item-header">
                  <strong>{analysis.instrumentName}</strong>
                  {/* 🎯 è la stessa icona della voce Wishlist in sidebar: qui
                      dice che su questo titolo esiste già una decisione di
                      acquisto, quindi è uno di quelli su cui il motore può
                      davvero operare. */}
                  {inWishlist.has(analysis.instrumentId) && (
                    <span className="briefing-chain__wishlist" title="Questo titolo è un candidato attivo in wishlist">
                      🎯 in wishlist
                    </span>
                  )}
                  {analysis.rating && <Badge tone="accent">{analysis.rating}</Badge>}
                  {/* Il prezzo di ALLORA, non quello di adesso: e' il numero su
                      cui quel giudizio e' stato dato, e mostrare il corrente
                      riscriverebbe la storia a ogni apertura della pagina. */}
                  {priceById.get(analysis.instrumentId) && (
                    <span
                      className="briefing-chain__price"
                      title={`Prezzo noto al momento del resoconto${
                        priceById.get(analysis.instrumentId)!.priceAt
                          ? ` (${formatDateTime(priceById.get(analysis.instrumentId)!.priceAt as string)})`
                          : ''
                      }`}
                    >
                      allora {priceById.get(analysis.instrumentId)!.priceAtBriefing}{' '}
                      {priceById.get(analysis.instrumentId)!.priceCurrency ?? ''}
                    </span>
                  )}
                </div>
                {analysis.executiveSummary && <p>{analysis.executiveSummary}</p>}
                <Link
                  className="ai-analysis__full-report-link"
                  to={`/watchlists/analyses/${analysis.instrumentId}/${analysis.analysisId}`}
                >
                  Report completo →
                </Link>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}
